import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table as TableComposable,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
} from '@patternfly/react-table';
import { Skeleton } from '@patternfly/react-core';

import './ScheduleTableSkeleton.scss';

const SKELETON_ROW_COUNT = 8;
const SKELETON_CELL_HEIGHT = 24;
const DATETIME_BASE_WIDTH = 220;
const OTHER_CELL_BASE_WIDTH = 70;
const VARIANCE_MIN = 0.6;
const VARIANCE_MAX = 1.4;
const DATETIME_VARIANCE_MIN = 0.9;
const DATETIME_VARIANCE_MAX = 1.4;

const COL_BASES = [
  20, // expand
  20, // radio
  24, // icon
  DATETIME_BASE_WIDTH, // Start Time
  DATETIME_BASE_WIDTH, // End time
  OTHER_CELL_BASE_WIDTH, // Day pattern
  OTHER_CELL_BASE_WIDTH, // Language
  OTHER_CELL_BASE_WIDTH, // Version
  OTHER_CELL_BASE_WIDTH, // Status
  OTHER_CELL_BASE_WIDTH, // Guaranteed to run
] as const;
const HEADER_SKELETON_WIDTHS = [90, 75, 80, 65, 55, 55, 105] as const;

const HEADER_KEYS: readonly string[] = [
  'skeleton-header-start',
  'skeleton-header-end',
  'skeleton-header-day-pattern',
  'skeleton-header-lang',
  'skeleton-header-version',
  'skeleton-header-status',
  'skeleton-header-guaranteed',
];

export default function ScheduleTableSkeleton() {
  const { t } = useTranslation();

  const { bodyWidths, rowKeys, cellKeys } = useMemo(() => {
    const widths: number[][] = [];
    const rowKeyList: string[] = [];
    const cellKeyGrid: string[][] = [];
    for (let r = 0; r < SKELETON_ROW_COUNT; r += 1) {
      rowKeyList.push(`skeleton-row-${r}`);
      cellKeyGrid[r] = [];
      widths[r] = COL_BASES.map((base, c) => {
        cellKeyGrid[r].push(`skeleton-cell-${r}-${c}`);
        if (base === 20 || base === 24) return base;
        const isDatetime = c === 3 || c === 4;
        const min = isDatetime ? DATETIME_VARIANCE_MIN : VARIANCE_MIN;
        const max = isDatetime ? DATETIME_VARIANCE_MAX : VARIANCE_MAX;
        const multiplier = min + Math.random() * (max - min);
        return Math.round(base * multiplier);
      });
    }
    return { bodyWidths: widths, rowKeys: rowKeyList, cellKeys: cellKeyGrid };
  }, []);

  return (
    <TableComposable
      aria-label={t('Table loading')}
      className="course-progress__table schedule-table-skeleton"
      variant="compact"
      isStriped
    >
      <Thead>
        <Tr>
          <Th aria-label="blank" modifier="fitContent" />
          <Th aria-label="blank" modifier="fitContent" />
          <Th modifier="fitContent" style={{ paddingRight: 0 }} />
          {HEADER_SKELETON_WIDTHS.map((base, i) => (
            <Th
              key={HEADER_KEYS[i]}
              modifier={i === 0 || i === 1 ? 'nowrap' : undefined}
            >
              <Skeleton
                width={`${base}px`}
                height={`${SKELETON_CELL_HEIGHT}px`}
                screenreaderText={t('Loading')}
              />
            </Th>
          ))}
        </Tr>
      </Thead>
      <Tbody>
        {rowKeys.map((rowKey, rowIndex) => (
          <Tr key={rowKey}>
            <Td modifier="fitContent">
              <Skeleton
                width="20px"
                height={`${SKELETON_CELL_HEIGHT}px`}
                screenreaderText={t('Loading')}
              />
            </Td>
            <Td modifier="fitContent">
              <Skeleton
                width="20px"
                height={`${SKELETON_CELL_HEIGHT}px`}
                screenreaderText={t('Loading')}
              />
            </Td>
            <Td modifier="fitContent" style={{ paddingRight: 0 }}>
              <Skeleton
                width="24px"
                height={`${SKELETON_CELL_HEIGHT}px`}
                screenreaderText={t('Loading')}
              />
            </Td>
            {bodyWidths[rowIndex].slice(3).map((w, colIndex) => (
              <Td key={cellKeys[rowIndex][colIndex + 3]}>
                <Skeleton
                  width={`${w}px`}
                  height={`${SKELETON_CELL_HEIGHT}px`}
                  screenreaderText={t('Loading')}
                />
              </Td>
            ))}
          </Tr>
        ))}
      </Tbody>
    </TableComposable>
  );
}
